import {
  Button,
  Link as ChakraLink,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { BrandIcon } from '@blockpulse3/ui/brand-theme';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { schema, signUpFormDefaults } from './schema';
import { ISignUpForm } from './types';

type Props = {
  /* ** SignUp form default values, can hold email from redirect ** */
  defaultValues?: ISignUpForm;
  /* ** Disabled email field, from redirection ** */
  isDisabled?: boolean;
  /* ** Callback loading state ** */
  loading: boolean;
  /* ** Callback submit ** */
  onSubmit: (data: ISignUpForm) => void;
};

/**
 * SignUpForm.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SignUpForm({
  isDisabled = false,
  defaultValues = signUpFormDefaults,
  loading,
  onSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSignUpForm = useTranslations('SignUpForm');

  const { register, formState, handleSubmit } = useForm<ISignUpForm>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const handleSignUpSubmit: SubmitHandler<ISignUpForm> = (data) => {
    onSubmit(data);
  };

  return (
    <Container bg={{ base: 'transparent', md: 'bg-surface' }} maxW="lg">
      <Stack spacing="8">
        <Stack align="start" spacing="6">
          <BrandIcon />
          <Heading alignSelf="start" data-cy="signup-title" size="lg">
            {i18nSignUpForm('FormTitle')}
          </Heading>
        </Stack>
        <form onSubmit={handleSubmit(handleSignUpSubmit)}>
          <Stack spacing="6">
            <Stack spacing="4">
              <fieldset disabled={isDisabled}>
                <FormControl isInvalid={!!formState.errors?.email}>
                  <FormLabel htmlFor="email">{i18nSignUpForm('EmailField')}</FormLabel>
                  <Input data-cy="email-input" id="email" type="text" {...register('email')} />
                  <ErrorMessage error={formState.errors?.email} />
                </FormControl>
              </fieldset>
              <FormControl isInvalid={!!formState.errors?.password}>
                <FormLabel htmlFor="password">{i18nSignUpForm('PasswordField')}</FormLabel>
                <Input
                  data-cy="password-input"
                  id="password"
                  type="password"
                  {...register('password')}
                />
                <ErrorMessage error={formState.errors?.password} />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="confirmPassword">
                  {i18nSignUpForm('ConfirmPasswordField')}
                </FormLabel>
                <Input
                  data-cy="confirm-input"
                  id="confirmPassword"
                  isInvalid={!!formState.errors?.confirmPassword}
                  type="password"
                  {...register('confirmPassword')}
                />
                <ErrorMessage error={formState.errors?.confirmPassword} />
              </FormControl>
              <FormControl alignItems="center" display="flex">
                <Stack spacing="0">
                  <Checkbox
                    data-cy="terms-checkbox"
                    isInvalid={!!formState.errors?.conditions}
                    {...register('conditions')}
                  >
                    <ChakraLink
                      isExternal
                      color="secondary"
                      data-cy="terms-link"
                      href="assets/Blockpulse_CGU.pdf"
                      textDecoration="underline"
                    >
                      {i18nSignUpForm('ContractConditionField')}
                    </ChakraLink>
                  </Checkbox>
                  <ErrorMessage error={formState.errors?.conditions} />
                </Stack>
              </FormControl>
            </Stack>
            <Stack spacing="2">
              <Button isLoading={loading} type="submit">
                {i18nGeneral('Submit')}
              </Button>
              <Link to={routes.login.href}>
                <Text color="secondary" textAlign="center" textDecoration="underline">
                  {i18nSignUpForm('AlreadyAccount')}
                </Text>
              </Link>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

export type SignUpFormProps = Props;
